import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Collapse,
  Divider,
  Tooltip,
  IconButton,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckIcon from "@mui/icons-material/Check";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import { Candidate, getCandidateLabel } from "../../services/preventiviApi";
import { MatchTypeChip, CategoryChip, DisciplineChip } from "../common/StatusChip";
import { formatDateRome } from "../../utils/dateFormat";

interface CandidateCardProps {
  candidate: Candidate;
  selected?: boolean;
  onChoose?: (candidate: Candidate) => void;
  /** Called when the user clicks the remove button on an already-selected card. */
  onDeselect?: () => void;
  showChooseButton?: boolean;
  compact?: boolean;
  onDetails?: () => void;
  extraActions?: React.ReactNode;
  /** Optional badge chip rendered inline in the chip row (e.g. "altra cat.") */
  badge?: React.ReactNode;
}

const _fmt = (v: any, digits = 2): string => {
  if (v == null) return "—";
  const n = parseFloat(String(v));
  if (isNaN(n)) return "—";
  return n.toFixed(digits);
};

const _fmtPosa = (v: any): string => _fmt(v, 2);

export const CandidateCard: React.FC<CandidateCardProps> = ({
  candidate,
  selected,
  onChoose,
  onDeselect,
  showChooseButton = true,
  compact = false,
  onDetails,
  extraActions,
  badge,
}) => {
  const [expanded, setExpanded] = useState(false);
  const meta = candidate.metadata ?? {};

  const ageLabel = meta.item_age_days != null
    ? `${meta.item_age_days} giorni fa`
    : formatDateRome(meta.created_at) ?? null;

  const hasCosts = meta.costo_mat_unitario != null
    || meta.tempo_posa_ore != null
    || meta.costo_mano_unitario != null
    || meta.prezzo != null;

  const p = compact ? 1 : 1.5;

  return (
    <Box
      sx={{
        border: "1.5px solid",
        borderColor: selected ? "#1565C0" : "rgba(0,0,0,0.1)",
        borderRadius: "10px",
        p,
        bgcolor: selected ? "#1565C008" : "background.paper",
        transition: "all 0.15s",
        "&:hover": { borderColor: selected ? "#1565C0" : "#1565C040" },
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>

          {/* Title row with name + codice */}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, flexWrap: "wrap", mb: 0.25 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, lineHeight: 1.35, flexGrow: 1 }}
              noWrap={!expanded}
            >
              {getCandidateLabel(candidate)}
            </Typography>
            {meta.udm && (
              <Chip
                label={meta.udm}
                size="small"
                sx={{ height: 16, fontSize: "0.62rem", fontWeight: 600, bgcolor: "rgba(0,0,0,0.06)", flexShrink: 0 }}
              />
            )}
            {meta.codice && (
              <Typography variant="caption" sx={{ color: "text.secondary", fontFamily: "monospace", flexShrink: 0 }}>
                {meta.codice}
              </Typography>
            )}
          </Box>

          {/* Match type + score + category chips */}
          <Box sx={{ display: "flex", gap: 0.75, mt: 0.2, flexWrap: "wrap", alignItems: "center" }}>
            <MatchTypeChip
              matchType={candidate.match_type}
              matchPercent={candidate.match_percent}
            />
            <CategoryChip categoria={meta.categoria} />
            <DisciplineChip disciplina={meta.disciplina} />
            {badge}
          </Box>

          {/* Reasoning (always shown if present) */}
          {candidate.reasoning && (
            <Box sx={{ mt: 0.5, display: "flex", alignItems: "flex-start", gap: 0.4 }}>
              <HelpOutlineIcon sx={{ fontSize: "0.75rem", color: "text.disabled", mt: 0.15, flexShrink: 0 }} />
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4, fontStyle: "italic" }}>
                {candidate.reasoning}
              </Typography>
            </Box>
          )}

          {/* Source file + age */}
          <Box sx={{ display: "flex", gap: 1, mt: 0.4, flexWrap: "wrap", alignItems: "center" }}>
            {meta.source_file && (
              <Tooltip title={`Fonte: ${meta.source_file}`}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                  <FolderOpenIcon sx={{ fontSize: "0.7rem", color: "text.disabled" }} />
                  <Typography variant="caption" color="text.disabled" noWrap sx={{ maxWidth: 120 }}>
                    {meta.source_file}
                  </Typography>
                </Box>
              </Tooltip>
            )}
            {ageLabel && (
              <Tooltip title={`Aggiunto il ${formatDateRome(meta.created_at) ?? ""}`}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                  <AccessTimeIcon sx={{ fontSize: "0.7rem", color: "text.disabled" }} />
                  <Typography variant="caption" color="text.disabled">{ageLabel}</Typography>
                </Box>
              </Tooltip>
            )}
            {meta.anno && (
              <Typography variant="caption" color="text.disabled">
                Anno {meta.anno}
              </Typography>
            )}
          </Box>

          {/* 4 unit costs inline — same row, compact spacing */}
          {hasCosts && (
            <Box sx={{ display: "flex", gap: 1, mt: 0.5, flexWrap: "nowrap", alignItems: "center", overflow: "hidden" }}>
              {meta.costo_mat_unitario != null && (
                <Tooltip title="Costo materiale unitario">
                  <Typography variant="caption" sx={{ color: "#1565C0", fontWeight: 600, whiteSpace: "nowrap" }}>
                    Mat: {_fmt(meta.costo_mat_unitario)} €
                  </Typography>
                </Tooltip>
              )}
              {meta.tempo_posa_ore != null && (
                <Tooltip title="Tempo di posa unitario (ore)">
                  <Typography variant="caption" sx={{ color: "#2E7D32", fontWeight: 600, whiteSpace: "nowrap" }}>
                    Posa: {_fmtPosa(meta.tempo_posa_ore)} h
                  </Typography>
                </Tooltip>
              )}
              {meta.costo_mano_unitario != null && (
                <Tooltip title="Costo manodopera unitario">
                  <Typography variant="caption" sx={{ color: "#E65100", fontWeight: 600, whiteSpace: "nowrap" }}>
                    Man: {_fmt(meta.costo_mano_unitario)} €
                  </Typography>
                </Tooltip>
              )}
              {meta.prezzo != null && (
                <Tooltip title="Prezzo vendita unitario">
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "text.primary", whiteSpace: "nowrap" }}>
                    P.V.: {_fmt(meta.prezzo)} €
                  </Typography>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>

        {/* Right-side controls */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, flexShrink: 0 }}>
          {onDetails && (
            <Button
              size="small"
              variant="text"
              onClick={onDetails}
              sx={{
                fontSize: "0.72rem",
                minWidth: 0,
                px: 0.75,
                py: 0.25,
                color: "text.secondary",
                lineHeight: 1.5,
              }}
            >
              Dettagli
            </Button>
          )}
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{ color: "text.disabled", p: 0.25 }}
          >
            {expanded
              ? <ExpandLessIcon sx={{ fontSize: "1rem" }} />
              : <ExpandMoreIcon sx={{ fontSize: "1rem" }} />}
          </IconButton>
        </Box>
      </Box>

      {/* Expanded details */}
      <Collapse in={expanded}>
        <Box sx={{ mt: 1 }}>
          <Divider sx={{ mb: 1 }} />
          {meta.descrizione_breve && (
            <Box sx={{ mb: 0.75 }}>
              <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Descrizione breve
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.2, lineHeight: 1.6 }}>{meta.descrizione_breve}</Typography>
            </Box>
          )}
          {meta.description && (
            <Box sx={{ mb: 0.75 }}>
              <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Descrizione
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.2, lineHeight: 1.6 }}>{meta.description}</Typography>
            </Box>
          )}
          {meta.specs && (
            <Box sx={{ mb: 0.75 }}>
              <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Specifiche
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.2, lineHeight: 1.6 }}>{meta.specs}</Typography>
            </Box>
          )}
          <Typography variant="caption" color="text.disabled" sx={{ display: "block" }}>
            UID: {candidate.uid}
          </Typography>
          {meta.parent_uid && (
            <Typography variant="caption" color="text.disabled" sx={{ display: "block" }}>
              Revisione di: {meta.parent_uid}
            </Typography>
          )}
          {meta.recency_penalty != null && parseFloat(meta.recency_penalty) > 0 && (
            <Typography variant="caption" sx={{ color: "#FF6F00", display: "block" }}>
              −{(parseFloat(meta.recency_penalty) * 100).toFixed(1)}% penalità recenza
            </Typography>
          )}
        </Box>
      </Collapse>

      {/* Choose / deselect button */}
      {showChooseButton && (
        <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end", gap: 0.75 }}>
          {selected && onDeselect && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<CancelOutlinedIcon />}
              onClick={onDeselect}
              sx={{ borderRadius: "8px", fontSize: "0.78rem" }}
            >
              Rimuovi
            </Button>
          )}
          {!selected && onChoose && (
            <Button
              size="small"
              variant="contained"
              onClick={() => onChoose(candidate)}
              sx={{ borderRadius: "8px", fontSize: "0.78rem" }}
            >
              Scegli
            </Button>
          )}
          {selected && !onDeselect && onChoose && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<CheckIcon />}
              onClick={() => onChoose(candidate)}
              sx={{ borderRadius: "8px", fontSize: "0.78rem" }}
            >
              Selezionato
            </Button>
          )}
        </Box>
      )}

      {extraActions}
    </Box>
  );
};
